import { dayjs, toApiDate } from "@marekl/adminer/datetime";
import type { MembershipRole, Person, PersonPayload } from "../../../lib/queries/memberships";
import type { ID } from "../../../lib/id";
import { FIELDS } from "./draftFields";
import { NO_MERGE_STATE } from "./draftRows";
import type { DraftOrigin, DraftPerson, DraftRow, DraftState } from "./draftTypes";
import { isBlankNewRow, isRowDirty } from "./draftValidation";

/**
 * The rozpracovaná registrace as stored on the server
 * (models.SeasonRegistrationDraft, docs/rozpracovana-registrace.md §4).
 *
 * Only the rows that carry work are stored, each with what it was edited
 * FROM (baseline) and TO (person), so that opening it again is a three-way
 * merge with the roster as it is by then (draftRebase), never a replay of a
 * stale copy. The server refuses unknown fields (membership.draftPayload);
 * draftPayload.fixture.json (beside draftPersist.ts) is read by both sides' tests.
 */
export interface DraftPayload {
  v: 1;
  source_season_id: ID | null;
  next_new_id: number;
  /** The roster the draft last saw, so a returning draft tells a newcomer from somebody who was always there. */
  known_membership_ids: ID[];
  rows: DraftPayloadRow[];
}

type StoredPerson = PersonPayload;
type StoredOrigin =
  | { kind: "persisted"; membership_id: ID; person_id: ID }
  | { kind: "transfer"; person_id: ID; source_season_id: ID; reregistered?: boolean }
  | { kind: "new" };
type StoredBaseline = PersonPayload & { id: ID };

export interface DraftPayloadRow {
  key: string;
  origin: StoredOrigin;
  baseline: StoredBaseline | null;
  baseline_roles: MembershipRole[];
  person: StoredPerson;
  roles: MembershipRole[];
  removed: boolean;
  /** The server's value for each field still to be decided, as a string. */
  conflicts: Partial<Record<keyof DraftPerson, string>>;
  duplicate_of: ID | null;
}

function storedPerson(p: DraftPerson): StoredPerson {
  return { ...p, birth_date: toApiDate(p.birth_date) ?? "" };
}

function storedBaseline(p: Person): StoredBaseline {
  return {
    id: p.id,
    first_name: p.first_name,
    last_name: p.last_name,
    country_code: p.country_code,
    birth_date: p.birth_date,
    birth_number: p.birth_number,
    street: p.street,
    city: p.city,
    postal_code: p.postal_code,
    phone: p.phone,
  };
}

function storedOrigin(o: DraftOrigin): StoredOrigin {
  switch (o.kind) {
    case "persisted":
      return { kind: "persisted", membership_id: o.membershipId, person_id: o.personId };
    case "transfer":
      return {
        kind: "transfer",
        person_id: o.personId,
        source_season_id: o.sourceSeasonId,
        ...(o.reregistered ? { reregistered: true } : {}),
      };
    default:
      return { kind: "new" };
  }
}

/** A row worth keeping: work, or a question the merge left open. */
export function carriesWork(r: DraftRow): boolean {
  if (isBlankNewRow(r)) return false;
  return isRowDirty(r) || r.orphaned || r.duplicateOf !== null || Object.keys(r.conflicts).length > 0;
}

export function serializeDraft(state: DraftState): DraftPayload {
  const rows: DraftPayloadRow[] = [];
  for (const r of state.rows) {
    if (!carriesWork(r)) continue;
    const conflicts: DraftPayloadRow["conflicts"] = {};
    for (const [f, c] of Object.entries(r.conflicts) as [keyof DraftPerson, { theirs: DraftPerson[keyof DraftPerson] }][]) {
      conflicts[f] = f === "birth_date" ? (toApiDate(c.theirs as DraftPerson["birth_date"]) ?? "") : String(c.theirs ?? "");
    }
    rows.push({
      key: r.key,
      origin: storedOrigin(r.origin),
      baseline: r.baseline ? storedBaseline(r.baseline) : null,
      baseline_roles: [...r.baselineRoles],
      person: storedPerson(r.person),
      roles: [...r.roles],
      removed: r.removed,
      conflicts,
      duplicate_of: r.duplicateOf,
    });
  }
  return {
    v: 1,
    source_season_id: state.sourceSeasonId,
    next_new_id: state.nextNewId,
    known_membership_ids: state.rows.flatMap((r) => (r.origin.kind === "persisted" ? [r.origin.membershipId] : [])),
    rows,
  };
}

/** Whether a stored payload is one this page can open. */
export function parseDraftPayload(raw: unknown): DraftPayload | null {
  const p = raw as Partial<DraftPayload> | null;
  if (!p || p.v !== 1 || !Array.isArray(p.rows)) return null;
  return {
    v: 1,
    source_season_id: p.source_season_id ?? null,
    next_new_id: typeof p.next_new_id === "number" ? p.next_new_id : 1,
    known_membership_ids: Array.isArray(p.known_membership_ids) ? p.known_membership_ids : [],
    rows: p.rows,
  };
}

const date = (s: string) => (s ? dayjs(s) : null);

function draftPerson(p: StoredPerson): DraftPerson {
  return { ...p, country_code: p.country_code || "CZ", birth_date: date(p.birth_date) };
}

function restoredOrigin(o: StoredOrigin): DraftOrigin {
  switch (o.kind) {
    case "persisted":
      return { kind: "persisted", membershipId: o.membership_id, personId: o.person_id };
    case "transfer":
      return {
        kind: "transfer",
        personId: o.person_id,
        sourceSeasonId: o.source_season_id,
        ...(o.reregistered ? { reregistered: true } : {}),
      };
    default:
      return { kind: "new" };
  }
}

/**
 * The stored rows as draft rows, as of when they were saved. They are not
 * meant to be shown like this: RESTORE merges them with the roster as it is
 * now before anything renders.
 */
export function restoredRows(p: DraftPayload): DraftRow[] {
  return p.rows.map((r) => {
    const conflicts: DraftRow["conflicts"] = {};
    for (const f of FIELDS) {
      const theirs = r.conflicts?.[f];
      if (theirs === undefined) continue;
      conflicts[f] = { theirs: f === "birth_date" ? date(theirs) : theirs };
    }
    return {
      ...NO_MERGE_STATE,
      key: r.key,
      origin: restoredOrigin(r.origin),
      baseline: r.baseline ? { ...r.baseline, cmf_id: null, photo_uploaded: false } : null,
      baselineRoles: [...r.baseline_roles],
      person: draftPerson(r.person),
      roles: [...r.roles],
      removed: r.removed,
      isLeader: false,
      serverErrors: {},
      conflicts,
      duplicateOf: r.duplicate_of,
    };
  });
}

/** How many people a stored draft changes: the „12 změn" on the resume card. */
export function draftChangeCount(p: DraftPayload): number {
  return p.rows.length;
}
